import type { AdvertisementRentStatus, LandTechnicalFeature, UploadEntity } from '@dobrozem/sdk';

import headerStyles from '@/views/Header/Header.module.scss';

export const SITE_URL = 'https://dobrozem.com.ua';

export function beautifyNumber(x: number | string) {
  return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ' ');
}

export function cleanPhoneNumber(phoneNumber: string): string {
  return phoneNumber.replace(/[ ()]/g, '');
}

export const namePattern = /^[A-Za-zА-ЩЬЮЯҐЄІЇа-щьюяґєії]+$/;

export const waitFor = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export function scrollToAnchor(anchorId: string, headerHeight: number = 0): void {
  const anchorElement = document.getElementById(anchorId);

  const header = document.querySelector<HTMLElement>(`.${headerStyles.wrapper}`);

  if (anchorElement) {
    const offset =
      anchorElement.getBoundingClientRect().top +
      window.scrollY -
      (header?.offsetHeight ?? headerHeight);

    window.scrollTo({ behavior: 'smooth', top: offset });
  } else {
    console.error(`Anchor element with id '${anchorId}' not found.`);
  }
}

export const technicalFeaturesDictionary: Record<LandTechnicalFeature, string> = {
  ADJACENT_TO_FOREST: 'Прилягає до лісу',
  ADJACENT_TO_RAILWAY_TRACK: 'Прилягає до залізничної колії',
  ADJACENT_TO_WATERBODY: 'Прилягає до водойми',
  ELECTRIC_NETWORKS: 'Електромережі',
  MAIN_PIPELINES: 'Магістральні трубопроводи',
  OTHER: 'Інше',
};

export const dateFormatterShort = Intl.DateTimeFormat('uk-UA', {
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  month: 'numeric',
  year: 'numeric',
});

export const uploadToSrc = (upload: UploadEntity | null | string | undefined) =>
  upload
    ? `${process.env.NEXT_PUBLIC_CORE_URL}/uploads/stream/${encodeURIComponent(typeof upload === 'string' ? upload : upload.storagePath)}`
    : `/no-data.svg`;

export const srcToUpload = (url: string): null | string => {
  const baseUrl = `${process.env.NEXT_PUBLIC_CORE_URL}/uploads/stream/`;

  // Перевіряємо, чи URL починається з базового URL
  if (url.startsWith(baseUrl)) {
    const encodedPath = url.substring(baseUrl.length); // Видаляємо базову частину

    try {
      return decodeURIComponent(encodedPath); // Декодуємо оригінальний шлях
    } catch (error) {
      console.error('Failed to decode URL:', error);

      return null;
    }
  }

  // Якщо URL не збігається з очікуваним форматом
  return null;
};

export const RENT_STATUS_DICTIONARY: Record<AdvertisementRentStatus, string> = {
  NOT_FOR_RENT: 'Не в орендi',
  RENTED: 'В орендi',
};

export function hasNullValues(obj: Record<string, any>): boolean {
  for (const key in obj) {
    if (obj[key] === null) {
      return true;
    }
  }

  return false;
}

export const loadingDataSelect = [{ label: 'Завантаження...', value: 'Завантаження...' }];

export const formatNumberValue = (value: string, numberLength: number) => {
  let formattedValue = value.replace(',', '.').replace(/[^0-9.]/g, '');

  if ((formattedValue.match(/\./g) || []).length > 1) {
    formattedValue = formattedValue.replace(/\.+$/, '');
  }

  // Дозволяємо лише до `numberLength` символів загалом
  if (formattedValue.length > numberLength) {
    formattedValue = formattedValue.slice(0, numberLength);
  }

  // Дозволяємо лише два знаки після крапки
  if (/^\d*\.?\d{0,4}$/.test(formattedValue)) {
    return formattedValue;
  } else {
    return formattedValue.slice(0, -1);
  }
};

export function isNewStatus(dateString: string): boolean {
  const inputDate = new Date(dateString);

  const currentDate = new Date();

  const differenceInMilliseconds = currentDate.getTime() - inputDate.getTime();

  const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);

  return differenceInHours < 24;
}

export const getSelectedNames = (
  selectedIds: null | string[] | undefined,
  allItems: { id: string; name: string }[],
): string => {
  if (!selectedIds || selectedIds.length === 0) return '';

  // Фільтруємо лише ті елементи, які відповідають обраним ID
  return allItems
    .filter((item) => selectedIds.includes(item.id.toString()))
    .map((item) => item.name) // Отримуємо лише `name`
    .join(', '); // Формуємо рядок через кому
};
